import type {
  SelectableCountry,
  SignUpOTPRequest,
  SignUpRequest,
} from '@/types';

import React from 'react';
import { Form, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSearchParams } from 'next/navigation';

import {
  Button,
  Link,
  MobileInput,
  RadioGroup,
  Stack,
  Text,
} from '@/components';
import { useInputAttributes, useSignUpOtp, useTranslation } from '@/hooks';
import { routes } from '@/statics';
import { englishOnlyParams, mobileRegex, yupValidator } from '@/utils';

const SIGN_UP_TYPES: SignUpRequest['type'][] = ['agent', 'customer'];

type MobileFormData = Pick<SignUpOTPRequest, 'mobile'> & {
  type: SignUpRequest['type'];
};

type MobileFormProps = {
  initialType?: SignUpRequest['type'];
  goNext: ({
    data,
    canSkip,
    otpTime,
  }: {
    data: MobileFormData & {
      country_phone_prefix: SignUpRequest['country_phone_prefix'];
      is_foreigner: SignUpRequest['is_foreigner'];
      token: SignUpRequest['token'];
    };
    canSkip: boolean;
    otpTime: number;
  }) => void;
};

const MobileForm: React.FC<MobileFormProps> = ({ initialType, goNext }) => {
  const { t } = useTranslation('pages.signUp.form.mobileForm');
  const searchParams = useSearchParams();
  const queryType = searchParams.get('type');
  const selectedInitialType =
    initialType ||
    (SIGN_UP_TYPES.includes(queryType as SignUpRequest['type'])
      ? (queryType as SignUpRequest['type'])
      : undefined);
  const isTypeLocked = Boolean(selectedInitialType);

  const [selectedCountry, setSelectedCountry] =
    React.useState<SelectableCountry>('iran');

  const countryCodes: Record<
    SelectableCountry,
    SignUpRequest['country_phone_prefix']
  > = {
    iran: '+98',
    iraq: '+964',
    afghanistan: '+93',
  };

  const validationSchema = yupValidator.object().shape({
    type: yupValidator
      .string()
      .oneOf(['agent', 'customer'])
      .label(t('type'))
      .required(),
    mobile: yupValidator
      .string()
      .test({ ...englishOnlyParams })
      .matches(mobileRegex[selectedCountry], t('invalidNumber'))
      .label(t('mobile'))
      .required(),
  });

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: selectedInitialType,
    },
  });

  const filedInputProps = useInputAttributes<MobileFormData>(
    control,
    register,
    errors,
    {
      type: t('type'),
      mobile: t('mobile'),
    }
  );

  const { mutateAsync: getOtp, isPending } = useSignUpOtp();

  const onSubmit = (data: MobileFormData) => {
    void getOtp({
      mobile: data.mobile,
      country_code_prefix: countryCodes[selectedCountry],
    }).then((result) => {
      goNext({
        data: {
          ...data,
          country_phone_prefix: countryCodes[selectedCountry],
          is_foreigner: selectedCountry !== 'iran',
          token: result.token,
        },
        canSkip: result.can_skip_otp,
        otpTime: result.otp_validation_seconds,
      });
    });
  };

  return (
    <Stack flex={1} w="100%" h="100%" align="flex-start" gap="0">
      <Text
        fontSize={{ base: 'md', lg: 'xl' }}
        w="full"
        fontWeight="medium"
        textAlign="center"
      >
        {t('title')}
      </Text>
      <Form
        control={control}
        style={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {!isTypeLocked && (
          <Stack mt={{ base: '50px', md: '80px' }}>
            <RadioGroup
              stackProps={{ color: 'black' }}
              options={[
                { label: t('agent'), value: 'agent' },
                { label: t('customer'), value: 'customer' },
              ]}
              value={watch('type')}
              {...filedInputProps.type}
            />
          </Stack>
        )}
        <Stack w="100%" pt={8} flex={1}>
          <Stack flexGrow={1}>
            <MobileInput
              inputProps={{ ...filedInputProps.mobile }}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
            />
          </Stack>
          <Stack w="100%" mt={4} gap={6}>
            <Button
              isLoading={isPending}
              w="full"
              type="submit"
              onClick={(data) => void handleSubmit(onSubmit)(data)}
            >
              {t('sendCode')}
            </Button>
            <Button as={Link} variant="ghost" href={routes.signIn.url} w="full">
              {t('goToLogin')}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};
export default MobileForm;
